import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../_core/_services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { WalletDetailsDto, WalletTransactionDto } from '../../_core/_model/wallet';
import { DepositDto } from '../../_core/_model/deposits';
import { OrderDetailsDto, OrderDto, OrderSettlementDto } from '../../_core/_model/order';
import { WithdrawalDto } from '../../_core/_model/withdrawal';
import { FormatService } from '../../_core/_services/format';
import { UserDto } from '../../_core/_model/users';
import { AuthService } from '../../_core/_services/auth.service';
import { WalletTierData } from '../../_core/_model/compliance';
import { NotificationService } from '../../_core/_services/notification.service';

@Component({
  selector: 'app-manage-wallets',
  standalone: false,
  templateUrl: './manage-wallets.html',
  styleUrl: './manage-wallets.css',
})
export class ManageWallets implements OnInit {
  loading = false;
  loadingDeposit = false;
  loadingWithdrawals = false;
  loadingOrders = false;
  loadingTransactions = false;
  loadingSettlements = false;
  error = '';

  walletId!: string;
  wallet: WalletDetailsDto | null = null;
  walletDeposits?: DepositDto[] = [];
  walletWithdrawals?: WithdrawalDto[] = [];
  walletOrders?: OrderDetailsDto[] = [];
  walletSettlements?: OrderSettlementDto[] = [];
  walletTrades?: WalletTransactionDto[] = [];


  pageDeposit: number = 1;
  pageWithdrawals: number = 1;
  pageOrders: number = 1;
  pageTransactions: number = 1;
  pageSettlements: number = 1;


  backButtonRoute: string | null = null;
  backButtonId: string | null = null;

  selectedWithdrawal: WithdrawalDto | null = null;
  showWithdrawalDetailsModal = false;

  selectedDeposit: DepositDto | null = null;
  showDepositDetailsModal = false;

  constructor(
    private notifyService: NotificationService,
    private api: ApiService,
    private route: ActivatedRoute,
    private format: FormatService,
    private router: Router,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.walletId = this.route.snapshot.paramMap.get('walletId') ?? '';
    if (!this.walletId) {
      this.router.navigate(['/account'], {
          queryParams: { tab: 'wallets' },
          replaceUrl: true
      });
      return;
    }

    const extra = this.route.snapshot.paramMap.get('extra') ?? null;
    const extraId = this.route.snapshot.paramMap.get('extraId') ?? null;

    if (extra !== null && extraId !== null) {
      this.backButtonRoute = extra + "/manage";
      this.backButtonId = extraId;
    }

    this.loadWallet();
    this.loadDeposit();
    this.loadWithdrawals();
    this.loadOrders();
    this.loadTransactions();
  }

  loadDeposit() {
    this.loadingDeposit = true;
    this.error = '';

    this.api.getWalletDeposits(this.walletId, this.pageDeposit).subscribe({
      next: (res) => {
        this.walletDeposits = res?.data ?? [];
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet deposit details';
        this.notifyService.showError(this.error!, 'Error');
        this.walletDeposits = [];
      },
      complete: () => (this.loadingDeposit = false),
    });

  }
  loadWithdrawals() {
    this.loadingWithdrawals = true;
    this.error = '';

    this.api.getWalletWithdrawals(this.walletId, this.pageWithdrawals).subscribe({
      next: (res) => {
        this.walletWithdrawals = res?.data ?? [];
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet withdrawal details';
        this.notifyService.showError(this.error!, 'Error');
        this.walletWithdrawals = [];
      },
      complete: () => (this.loadingWithdrawals = false),
    });
  }
  loadOrders() {
    this.loadingOrders = true;
    this.error = '';

    this.api.getWalletOrders(this.walletId, this.pageOrders).subscribe({
      next: (res) => { 
        this.walletOrders = res?.data ?? [];
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet orders details';
        this.walletOrders = [];
      },
      complete: () => (this.loadingOrders = false),
    });
  }
  loadTransactions() {
    this.loadingTransactions = true;
    this.error = '';

    this.api.getWalletTransactions(this.walletId, this.pageTransactions).subscribe({
      next: (res) => {
        this.walletTrades = res?.data ?? [];
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet transactions details';
        this.walletTrades = [];
      },
      complete: () => (this.loadingTransactions = false),
    });
  }

  loadSettlements() {
    this.loadingSettlements = true;
    this.error = '';

    this.api.getWalletSettlements(this.walletId, this.pageSettlements).subscribe({
      next: (res) => {
        this.walletSettlements = res?.data ?? [];
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet settlements details';
        this.walletSettlements = [];
      },
      complete: () => (this.loadingSettlements = false),
    });
  }

  loadWallet() {
    this.loading = true;
    this.error = '';

    this.api.getSingleWallet(this.walletId).subscribe({
      next: (res) => {
        this.wallet = res?.data ?? null;
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallet';
        this.notifyService.showError(this.error!, 'Error');
        this.wallet = null;
      },
      complete: () => (this.loading = false),
    });
  }

  deposit() {
    if (!this.walletId) return;
    this.router.navigateByUrl(`/wallets/deposit/${this.walletId}`);
  }

  withdraw() {
    if (!this.walletId) return;
    this.router.navigateByUrl(`/wallets/withdraw/${this.walletId}`);
  }

  back() {
    if (this.backButtonRoute !== null && this.backButtonId !== null) {
      this.router.navigate([this.backButtonRoute, this.backButtonId]);
      return;
    }
    this.router.navigate(['/wallets']);
  }

  confirmDeposit(depositId: string) {
    this.router.navigateByUrl(`/wallets/deposit/confirmation/${depositId}`);
  }

  confirmWithdrawal(withdrawalId: string) {
    this.router.navigateByUrl(`/wallets/withdraw/confirmation/${withdrawalId}`);
  }

  getDepositAndWithdrawalStatusClass(status: string): string {
    return this.format.getDepositAndWithdrawalStatusClass(status, true);
  }

  getOrderStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status, true);
  }

  getSettlementStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status, true);
  }

  openWithdrawalDetails(item: WithdrawalDto): void {
    this.selectedWithdrawal = item;
    this.showWithdrawalDetailsModal = true;
  }

  closeWithdrawalDetails(): void {
    this.showWithdrawalDetailsModal = false;
    this.selectedWithdrawal = null;
  }

  openDepositDetails(item: DepositDto): void {
    this.selectedDeposit = item;
    this.showDepositDetailsModal = true;
  }

  closeDepositDetails(): void {
    this.showDepositDetailsModal = false;
    this.selectedDeposit = null;
  }
}